import { BallAction, FrameStencil, StencilType } from "../../../types";
import { findClosestStencil } from "../find-closest";
import {
  findPlayerOnAction,
  getBallActions,
  getNonAssociatedBallAndPossessionActions,
  getPlayerActions,
  getPlayerMarkers,
  getPossessionActions,
} from "../frame-stencil-filters";
import { DEFAULT_PASS_DURATION } from "../get-duration-from-action";
import { refreshMovements } from "../refresh-movements";
import { checkActionEndPoint } from "./check-action-end-point";
import { checkBallActionStartPoint } from "./check-ball-action-start-point";

export interface AddBallActionProps {
  action: BallAction;
  stencils: FrameStencil[];
}

export const addBallAction = ({ stencils, action }: AddBallActionProps): FrameStencil[] => {
  const { action: updatedBallAction, stencils: updatedStencils } = checkBallActionStartPoint({ action, stencils });

  // get all players and ballActions that aren't on another action's next
  const { next, newStencils } = checkActionEndPoint({
    action: updatedBallAction,
    actionsToCheck: [
      ...getPlayerMarkers(updatedStencils),
      ...getNonAssociatedBallAndPossessionActions(updatedStencils),
    ],
    stencils: updatedStencils,
  });

  const actionWithNext: BallAction = ((): BallAction => {
    switch (next?.stencilType) {
      case StencilType.ACTION:
        return { ...updatedBallAction, nextBallAction: next.id };

      case StencilType.MARKER: {
        const playersNextPass = getBallActions(newStencils).find((pass) => pass.prevPlayer === next.id);
        if (playersNextPass) return { ...updatedBallAction, nextBallAction: playersNextPass.id, nextPlayer: next.id };

        const playersNextPossession = getPossessionActions(newStencils).find((s) =>
          next.movements.map((m) => m.id).includes(s.id)
        );

        if (playersNextPossession)
          return { ...updatedBallAction, nextBallAction: playersNextPossession.id, nextPlayer: next.id };

        return { ...updatedBallAction, nextPlayer: next.id };
      }

      default:
        return updatedBallAction;
    }
  })();

  /** find closest playerAction endPoint to new endPoint */
  const closestPlayerActionToEndPoint = findClosestStencil({
    target: { id: action.id, point: action.endPos },
    stencils: getPlayerActions(stencils),
    stencilsPointAccessor: (s) => s.endPos,
  });

  /** if the endPoint of the new ballAction falls on the endPoint of a playerAction, set playerActionEnd to the found
   * endPoint to associate the pass with any players that may be moving along the playerAction */
  const actionUpdatedEndTime = Math.max(actionWithNext.endTime, closestPlayerActionToEndPoint?.endTime || 0);
  const actionWithPlayerActionEnd: BallAction = {
    ...actionWithNext,
    playerActionEnd: closestPlayerActionToEndPoint?.id || null,
    endTime: actionUpdatedEndTime,
    startTime: actionUpdatedEndTime - DEFAULT_PASS_DURATION,
  };

  /** find any players that are associated with the closestPlayerActionToEndPoint */
  const playerOnClosestPlayerActionEnd = closestPlayerActionToEndPoint
    ? findPlayerOnAction({ stencils, action: closestPlayerActionToEndPoint })
    : null;

  /** if found and ball action doesn't already have a nextPlayer, add to the ball action's nextPlayer prop */
  const actionWithNextPlayer: BallAction = {
    ...actionWithPlayerActionEnd,
    ...(playerOnClosestPlayerActionEnd && !actionWithPlayerActionEnd.nextPlayer
      ? { nextPlayer: playerOnClosestPlayerActionEnd.id }
      : {}),
  };

  return refreshMovements([...newStencils, actionWithNextPlayer]);
};
